import React from 'react';
import { formatNumber } from './format';
import {
  PhaseRibbon, PhaseShading, RIBBON_GAP, RIBBON_H, phaseAt, phaseSegments, phaseText,
} from './phases';
import { EMPTY_TEXT } from '../copy';

// Bespoke SVG line/area chart. Kept (instead of replacing with Recharts'
// LineChart) because it's tightly tuned for the dashboard's tooltip
// style + label density and behaves identically across browsers.
//
// The plot is drawn at the container's real pixel width (measured with a
// ResizeObserver), not stretched from a fixed 700-unit viewBox. Stretching made
// the axis type scale with the card: ~15px on a full-width chart and ~5px on a
// half-width one, which was unreadable. `height` is therefore the true height.
//
// Props:
//   data         — array of objects, each with `labelKey` (date string) + line values
//   labelKey     — name of the date field on each row, e.g. 'date'
//   lines        — [{key, label}, ...]
//   colors       — same length as `lines`; any CSS colour, including var(--series-n)
//   height       — px (default 200)
//   formatY      — y-axis tick formatter; falls back to formatNumber
//   formatValue  — tooltip VALUE formatter; falls back to formatNumber.
//                  Separate from formatY on purpose. The axis and the tooltip want
//                  different precision (the cost chart rounds its axis to whole euros
//                  above 10 and would lose the cents in the readout), so defaulting one
//                  to the other would silently change every existing chart. Pass it
//                  where the value carries a UNIT — a duration rendered as "45,000" is
//                  indistinguishable from a count.
//   tooltipExtra — optional fn(row) → JSX appended to the tooltip body
//   legend       — optional boolean: draw the series legend under the plot
//   formatX      — optional fn(labelValue) → string for the x ticks and tooltip title.
//                  Default: 'MM-DD' for a date, 'MM-DD HH:mm' for a timestamp.
//   yMax         — optional floor for the top of the scale, e.g. 100 on a percentage
//                  chart so 12% does not fill the plot. Data above it still fits.
//   scaleFloor   : the smallest top the scale may have (default 1). Counts want the
//                  floor, or a quiet series of 0s and 1s gets fractional ticks; a series
//                  whose values are fractions of a cent passes 0, or it draws along the
//                  baseline of a 0..1 scale.
//   refLines     — optional [{ value, label, color? }]: dashed horizontal guides (an
//                  alarm threshold, a configured ceiling), amber unless `color` says
//                  otherwise. They count towards the scale, so a ceiling far above the
//                  data is still drawn rather than clipped.
//   gapOnNull    — when true, a `null` value breaks the line instead of drawing a dip
//                  to zero. For series where "no datapoint" and "zero" mean different
//                  things (Aurora ACU, a service copy that was offline). Off by
//                  default because every pivoted chart relies on a missing key being 0.
//                  A point with a gap on both sides is drawn as a dot, since a line
//                  needs two points and it would otherwise not be drawn at all.
//   gapLabel     : tooltip text for a gap, as a string or fn(lineKey, rowIndex) → string
//                  when the reason differs point by point (a copy that was offline vs a
//                  datapoint that is missing). Default 'no data'.
//   phases       : optional, a load test's phases in row units (charts/phases.jsx
//                  phasesOn): a labelled strip above the plot, a faint band behind the
//                  series, and the phase of the hovered point in the tooltip.
//   stacked      : draw the series stacked, each filled from the one below it (the first
//                  line at the bottom), the scale fitting their total. The tooltip still
//                  reads each series' own value. A null counts as 0.
const TICK_FONT = 11;
const CHAR_W = 6.3;      // average glyph advance at TICK_FONT, for sizing the gutter
const PAD_TOP = 10;
const PAD_BOTTOM = 26;   // room for the x ticks
const PAD_RIGHT = 16;

function niceStep(raw) {
  const exp = Math.floor(Math.log10(raw));
  const f = raw / 10 ** exp;
  const nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return nf * 10 ** exp;
}

function formatTick(s, formatX) {
  if (!s) return '';
  if (formatX) return formatX(s);
  if (s.includes(' ') || s.includes('T')) {
    const dt = new Date(s.replace(' ', 'T'));
    return `${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')} ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
  }
  return s.slice(5);
}

export default function TimeChart({
  data, labelKey, lines, colors, height = 200, formatY, formatValue, tooltipExtra,
  legend = false, formatX, yMax, refLines, gapOnNull = false, gapLabel = 'no data',
  scaleFloor = 1, phases, stacked = false,
}) {
  const [hover, setHover] = React.useState(null);
  const [w, setW] = React.useState(700);
  const wrapRef = React.useRef(null);
  const hasData = Boolean(data && data.length);

  React.useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = (width) => {
      const px = Math.round(width);
      if (px > 0) setW(prev => (Math.abs(prev - px) > 1 ? px : prev));
    };
    measure(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(entries => measure(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasData]);

  if (!hasData) {
    return <div className="tile-empty">{EMPTY_TEXT}</div>;
  }

  // A stack has no gaps: a missing state is no user in it.
  const isGap = (v) => !stacked && gapOnNull && (v === null || v === undefined);
  const num = (v) => Number(v) || 0;
  // Stacked: where each series starts, the total of the ones below it.
  const stackBase = stacked
    ? lines.map((_, li) => data.map(d => lines.slice(0, li).reduce((a, l) => a + num(d[l.key]), 0)))
    : null;
  const valueAt = (li, i) => (stacked ? stackBase[li][i] + num(data[i][lines[li].key])
    : data[i][lines[li].key]);
  const allVals = stacked
    ? data.map(d => lines.reduce((a, l) => a + num(d[l.key]), 0))
    : data.flatMap(d => lines.map(l => d[l.key] || 0));
  const refs = (refLines || []).filter(r => Number.isFinite(r?.value));
  const dataMax = Math.max(...allVals, ...refs.map(r => r.value), yMax || 0);
  const fmtY = formatY || formatNumber;

  // Round tick steps (1, 2, 2.5 or 5 × 10ⁿ) and a scale top that lands on one, so the
  // axis reads 0 / 500 / 1.0K / 1.5K rather than quarters of whatever the peak was.
  // A series of whole numbers (counts) never gets a fractional step: the default
  // formatter rounds, so 0.25 / 0.5 / 0.75 would print as 0 / 1 / 1.
  const integral = allVals.every(Number.isInteger);
  // An all-zero series has no scale of its own; it keeps the 0..1 one.
  const top = Math.max(dataMax, scaleFloor) || 1;
  const tickStep = Math.max(integral ? 1 : 0, niceStep(top / 4));
  const maxVal = Math.max(tickStep, Math.ceil(top / tickStep - 1e-9) * tickStep);
  const yTicks = [];
  for (let i = 0; i * tickStep <= maxVal + tickStep / 2; i += 1) {
    const v = Math.round(i * tickStep * 1e9) / 1e9;   // no 0.30000000000000004
    yTicks.push({ val: v, text: String(fmtY(v)) });
  }
  // Left gutter sized to the widest tick, so "32.5 KiB/s" is not pushed off the card.
  const padL = Math.max(34, Math.ceil(Math.max(...yTicks.map(t => t.text.length)) * CHAR_W) + 10);
  const plotW = Math.max(40, w - padL - PAD_RIGHT);
  // The phases' strip sits above the plot, which starts below it.
  const hasPhases = Boolean(phases && phases.length);
  const padTop = PAD_TOP + (hasPhases ? RIBBON_H + RIBBON_GAP : 0);
  const plotH = Math.max(40, height - padTop - PAD_BOTTOM);
  const stepX = data.length > 1 ? plotW / (data.length - 1) : 0;
  const xOf = (i) => padL + i * stepX;
  const yOf = (v) => padTop + plotH - ((v || 0) / maxVal) * plotH;
  const baseY = padTop + plotH;
  const phaseSegs = hasPhases ? phaseSegments(phases, xOf) : [];

  // Runs of consecutive plottable points. Without gapOnNull that is always one run.
  const segmentsFor = (li) => {
    const runs = [];
    let run = [];
    data.forEach((d, i) => {
      if (isGap(d[lines[li].key])) {
        if (run.length) runs.push(run);
        run = [];
        return;
      }
      run.push({ x: xOf(i), y: yOf(valueAt(li, i)) });
    });
    if (run.length) runs.push(run);
    return runs;
  };

  // A stacked series' band: its top edge, then back along the top of the one below.
  const makeStack = (li) => {
    const topEdge = data.map((_, i) => `${xOf(i).toFixed(1)},${yOf(valueAt(li, i)).toFixed(1)}`);
    const below = data.map((_, i) => `${xOf(i).toFixed(1)},${yOf(stackBase[li][i]).toFixed(1)}`)
      .reverse();
    return `M${topEdge.join(' L')} L${below.join(' L')} Z`;
  };

  const makePath = (runs) => runs.map(pts => pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')).join(' ');

  const makeArea = (runs) => runs.map(pts => `M${pts[0].x.toFixed(1)},${baseY} `
    + pts.map(p => `L${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')
    + ` L${pts[pts.length - 1].x.toFixed(1)},${baseY} Z`).join(' ');

  // One series gets a soft fill; overlapping fills from several series stack into a
  // grey slab that hides the lines, so they fade out as the count grows.
  const areaOpacity = lines.length === 1 ? 0.1 : lines.length === 2 ? 0.05 : 0;

  // As many x ticks as fit: a label is ~7 characters wide plus breathing room.
  const sample = formatTick(data[data.length - 1][labelKey], formatX);
  const maxTicks = Math.max(2, Math.floor(plotW / (sample.length * CHAR_W + 28)));
  const step = Math.max(1, Math.ceil(data.length / maxTicks));
  const xTickIdx = data.map((_, i) => i).filter(i => i % step === 0);
  const lastIdx = data.length - 1;
  // Keep the final point labelled, dropping the regular tick just before it if the two
  // would collide. Measured in pixels, and with room for the last label being
  // right-aligned (it is, at the edge), which moves it a whole label-width to the left.
  if (xTickIdx[xTickIdx.length - 1] !== lastIdx) {
    const labelW = sample.length * CHAR_W + 12;
    if (xTickIdx.length > 1 && (lastIdx - xTickIdx[xTickIdx.length - 1]) * stepX < labelW * 1.5) {
      xTickIdx.pop();
    }
    xTickIdx.push(lastIdx);
  }

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * w;
    const idx = stepX > 0 ? Math.round((svgX - padL) / stepX) : 0;
    setHover(Math.max(0, Math.min(lastIdx, idx)));
  };

  const hoverX = hover !== null ? xOf(hover) : 0;
  const hoverData = hover !== null ? data[hover] : null;
  const formatLabel = (d) => (d ? formatTick(d[labelKey], formatX) : '');

  return (
    <div className="timechart" ref={wrapRef}>
      <svg viewBox={`0 0 ${w} ${height}`} className="timechart-svg"
        onMouseMove={handleMouseMove} onMouseLeave={() => setHover(null)}>
        <PhaseShading segments={phaseSegs} top={padTop} bottom={baseY} />
        {yTicks.map((t, i) => (
          <g key={i}>
            <line x1={padL} y1={yOf(t.val)} x2={w - PAD_RIGHT} y2={yOf(t.val)} stroke="var(--chart-grid)" strokeWidth={1} />
            <text x={padL - 8} y={yOf(t.val) + 4} textAnchor="end" fontSize={TICK_FONT} fill="var(--chart-axis)">
              {t.text}
            </text>
          </g>
        ))}
        {refs.map((r, i) => (
          <line key={`ref-${i}`} x1={padL} y1={yOf(r.value)} x2={w - PAD_RIGHT} y2={yOf(r.value)}
                stroke={r.color || 'var(--warn)'} strokeWidth={1} strokeDasharray="5,4"
                opacity={0.8} />
        ))}
        {lines.map((l, li) => {
          const runs = segmentsFor(li);
          if (runs.length === 0) return null;
          const strokes = runs.filter(pts => pts.length > 1);
          const lone = runs.filter(pts => pts.length === 1);
          return (
            <g key={l.key}>
              {stacked && data.length > 1 && (
                <path d={makeStack(li)} fill={colors[li]} fillOpacity={0.32} />
              )}
              {!stacked && areaOpacity > 0 && strokes.length > 0 && (
                <path d={makeArea(strokes)} fill={colors[li]} opacity={areaOpacity} />
              )}
              {strokes.length > 0 && (
                <path d={makePath(strokes)} fill="none" stroke={colors[li]}
                      strokeWidth={stacked ? 1.5 : 2}
                      strokeLinejoin="round" strokeLinecap="round" />
              )}
              {lone.map(([p]) => (
                <circle key={p.x.toFixed(1)} cx={p.x} cy={p.y} r={2.5} fill={colors[li]} />
              ))}
            </g>
          );
        })}
        {/* Threshold labels go over the series, with a halo in the card colour: a
            service running AT its threshold is exactly when the label must be legible. */}
        {refs.filter(r => r.label).map((r, i) => (
          /* Above its line, or below it when above would reach the phases' strip. */
          <text key={`ref-label-${i}`} x={w - PAD_RIGHT}
                y={hasPhases && yOf(r.value) - 5 - TICK_FONT < padTop ? yOf(r.value) + TICK_FONT + 3 : yOf(r.value) - 5}
                textAnchor="end"
                fontSize={TICK_FONT} fill={r.color || 'var(--warn)'}
                stroke="var(--surface)" strokeWidth={3} strokeLinejoin="round" paintOrder="stroke">
            {r.label}
          </text>
        ))}
        {hover !== null && (
          <g>
            {/* Up through the phases' strip, so the phase under the cursor is easy to find. */}
            <line x1={hoverX} y1={hasPhases ? PAD_TOP : padTop} x2={hoverX} y2={baseY}
                  stroke="var(--chart-cursor)" strokeWidth={1} strokeDasharray="3,3" />
            {lines.map((l, li) => {
              const raw = hoverData?.[l.key];
              if (isGap(raw)) return null;
              return (
                <circle key={l.key} cx={hoverX} cy={yOf(valueAt(li, hover))} r={3.5}
                  fill={colors[li]} stroke="var(--surface)" strokeWidth={1.5} />
              );
            })}
          </g>
        )}
        <PhaseRibbon segments={phaseSegs} y={PAD_TOP} />
        {xTickIdx.map((idx) => {
          const text = formatLabel(data[idx]);
          // Centred under its point, except where centring would run off either edge.
          const half = (text.length * CHAR_W) / 2;
          const anchor = idx === lastIdx && lastIdx > 0 && xOf(idx) + half > w ? 'end'
            : xOf(idx) - half < 0 ? 'start' : 'middle';
          return (
            <text key={idx} x={xOf(idx)} y={height - 7} textAnchor={anchor}
                  fontSize={TICK_FONT} fill="var(--chart-axis)">
              {text}
            </text>
          );
        })}
      </svg>
      {hover !== null && hoverData && (
        <div
          className="chart-tooltip"
          style={{
            left: `${(hoverX / w) * 100}%`,
            transform: hoverX > w / 2 ? 'translateX(-110%)' : 'translateX(10%)',
          }}
        >
          <div className="chart-tooltip-title">{formatLabel(hoverData)}</div>
          {hasPhases && phaseAt(phases, hover) && (
            <div className="chart-tooltip-phase">{phaseText(phaseAt(phases, hover), hover)}</div>
          )}
          {/* A stack reads top down, as it is drawn. */}
          {(stacked ? lines.map((l, li) => [l, li]).reverse() : lines.map((l, li) => [l, li]))
            .map(([l, li]) => (
              <div key={l.key} className="chart-tooltip-row">
                <span className="chart-legend-swatch" style={{ background: colors[li] }} />
                <span className="chart-tooltip-label">{l.label}</span>
                <span className="chart-tooltip-value">
                  {isGap(hoverData[l.key])
                    ? (typeof gapLabel === 'function' ? gapLabel(l.key, hover) : gapLabel)
                    : (formatValue || formatNumber)(hoverData[l.key] || 0)}
                </span>
              </div>
            ))}
          {tooltipExtra && tooltipExtra(hoverData)}
        </div>
      )}
      {legend && (
        <div className="chart-legend mt-2">
          {lines.map((l, li) => (
            <span key={l.key} className="chart-legend-item">
              <span className="chart-legend-swatch" style={{ background: colors[li] }} />
              {l.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
